'use client';

import {config} from '@config';
import {datadogLogs} from '@datadog/browser-logs';
import {datadogRum} from '@datadog/browser-rum';
import groundcover, {type EventTypes} from '@groundcover/browser';
import _logger from '@lib/logger';
import {getHighestPrivilegeRole, isLinxAdminEmail} from '@lib/user';
import {useSession} from 'next-auth/react';
import {useEffect, useRef} from 'react';

const logger = _logger.child?.({module: `telemetry`}, {});

// The SDK only redacts by key name; this catches secrets by value: JWTs, PEM blocks, data URIs, mixed-case base64 blobs.
// ponytail: heuristic, add patterns as new secret shapes show up in Groundcover.
const SECRET_VALUE =
  /^eyJ[\w-]+\.[\w-]+\.[\w-]*$|-----BEGIN |^data:[^,]*;base64,|^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[\w+/-]{32,}={0,2}$/;

// Writes that carry credentials under connector-defined field names no key list can anticipate,
// so their request bodies are dropped whole.
const CREDENTIAL_WRITE_PATH =
  /^\/api\/proxy\/(integration-service\/(integrations|agent)|api-service\/(mcp-servers|oidc-connections|secrets)|notification-service\/slack\/connect)(\/|$)/;

const redactCredentialWriteBody = (event: EventTypes[keyof EventTypes]): EventTypes[keyof EventTypes] => {
  if (event.type !== `network`) return event;
  const {attributes} = event;
  const {pathname} = new URL(attributes.url.full, window.location.origin);
  if (attributes.http.method.toUpperCase() === `GET` || !CREDENTIAL_WRITE_PATH.test(pathname)) return event;
  return {
    ...event,
    attributes: {...attributes, gc: {...attributes.gc, request: {...attributes.gc.request, body: `[REDACTED]`}}},
  };
};

export const LogLevel = {
  INFO: 'info',
  ERROR: 'error',
  WARN: 'warn',
  DEBUG: 'debug',
} as const;

const isDatadogRumReady = () => typeof window !== 'undefined' && !!window.__DD_RUM_INITIALIZED;
const isGroundcoverReady = () => typeof window !== 'undefined' && !!window.__GC_RUM_INITIALIZED;

const logToGroundcover = (level: (typeof LogLevel)[keyof typeof LogLevel], message: string, context?: object) => {
  if (!isGroundcoverReady()) return;
  try {
    groundcover.logger[level](message, context as Record<string, unknown>);
  } catch (error) {
    logger?.error({error}, `Failed to forward log to Groundcover:`);
  }
};

// sendCustomEvent bypasses the SDK's privacy.redact, so apply SECRET_VALUE here. Action contexts are flat.
const redactSecrets = (attributes: Record<string, unknown>) =>
  Object.fromEntries(
    Object.entries(attributes).map(([key, value]) => [
      key,
      typeof value === `string` && SECRET_VALUE.test(value) ? `[REDACTED]` : value,
    ]),
  );

const sendGroundcoverEvent = (event: string, attributes: Record<string, unknown>) => {
  if (!isGroundcoverReady()) return;
  try {
    groundcover.sendCustomEvent({event, attributes: redactSecrets(attributes)});
  } catch (error) {
    logger?.error({error}, `Failed to send event to Groundcover:`);
  }
};

// Plain-object errors would otherwise all become `[object Object]` and share one fingerprint.
const toError = (error: unknown) => {
  if (error instanceof Error) return error;
  const message = (error as {message?: unknown} | null)?.message;
  return new Error(typeof message === `string` ? message : typeof error === `string` ? error : JSON.stringify(error));
};

const captureGroundcoverException = (error: unknown, context?: Record<string, any>) => {
  if (!isGroundcoverReady()) return;
  try {
    groundcover.captureException(toError(error), context);
  } catch (gcError) {
    logger?.error({error: gcError}, `Failed to forward error to Groundcover:`);
  }
};

export const addRumAction = (name: string, context: Record<string, unknown> = {}) => {
  if (isDatadogRumReady()) {
    datadogRum.addAction(name, context);
  }
  sendGroundcoverEvent(name, context);
};

export const addRumTiming = (name: string) => {
  if (isDatadogRumReady()) {
    datadogRum.addTiming(name, Date.now());
  }
  // Groundcover has no timing API; the event's own timestamp carries the timing.
  sendGroundcoverEvent(name, {});
};

export const addRumError = (error: unknown, context?: Record<string, any>) => {
  datadogRum.addError(error, context);
  captureGroundcoverException(error, context);
};

/**
 * Log a message to Datadog with the specified level and context
 */
export const logToDatadog = (
  message: string,
  level: (typeof LogLevel)[keyof typeof LogLevel] = LogLevel.INFO,
  context?: object,
) => {
  logToGroundcover(level, message, context);

  // Only log if Datadog is enabled and in a supported environment
  if (!config.datadog.enabled) {
    return;
  }

  switch (level) {
    case LogLevel.ERROR:
      datadogLogs.logger.error(message, context);
      break;
    case LogLevel.WARN:
      datadogLogs.logger.warn(message, context);
      break;
    default:
      datadogLogs.logger.info(message, context);
  }
};

/**
 * Capture an exception and send it to Datadog
 * This is a direct replacement for Sentry.captureException
 */
export const captureException = (error: Error | unknown, context?: Record<string, any>) => {
  // Extract error details
  const errorMessage = error instanceof Error ? error.message : String(error);
  const errorName = error instanceof Error ? error.name : `Unknown Error`;
  const errorStack = error instanceof Error ? error.stack : undefined;

  try {
    // Log to console in development
    if (config.datadog.enableDebugLogging) {
      logger?.debug({error: String(error)}, `Error captured by Datadog:`);
    }

    // Log to Datadog with error details and context
    datadogLogs.logger.error(
      `Exception captured`,
      {
        error_name: errorName,
        error_message: errorMessage,
        error_stack: errorStack,
        ...context,
      },
      error instanceof Error ? error : new Error(errorMessage),
    );

    // Also send to RUM if initialized
    if (window.__DD_RUM_INITIALIZED) {
      datadogRum.addError(error, {
        ...context,
      });
    }
  } catch (loggingError) {
    // Fallback if Datadog logging fails
    logger?.error({error: loggingError}, `Failed to log error to Datadog:`);
    logger?.error({error}, `Original error:`);
  }

  // Outside the Datadog try so neither SDK's failure can skip or be blamed on the other.
  captureGroundcoverException(error, context);
};

/**
 * Capture a message and send it to Datadog
 * This is a direct replacement for Sentry.captureMessage
 */
export const captureMessage = (
  message: string,
  level: 'info' | 'error' | 'warn' = `info`,
  context?: Record<string, any>,
) => {
  try {
    // Log to Datadog with the appropriate level
    logToDatadog(message, level, context);

    // Also send to RUM if initialized and if it's an error or warning
    if (window.__DD_RUM_INITIALIZED && (level === `error` || level === `warn`)) {
      datadogRum.addError(message, {
        ...context,
      });
    }
  } catch (loggingError) {
    // Fallback if Datadog logging fails
    logger?.error({error: loggingError}, `Failed to log message to Datadog:`);
    logger?.error({message}, `Original message:`);
  }

  if (level === `error` || level === `warn`) {
    captureGroundcoverException(message, context);
  }
};

declare global {
  interface Window {
    _DATADOG_SYNTHETICS_BROWSER?: boolean;
    __DD_LOGS_INITIALIZED?: boolean;
    __DD_RUM_INITIALIZED?: boolean;
    __GC_RUM_INITIALIZED?: boolean;
  }
}

type TenantConfig = Partial<{
  sessionSampleRate: number;
  sessionReplaySampleRate: number;
  startSessionReplayRecordingManually?: boolean;
}>;

// Configuration for all test tenants
const TEST_TENANT_CONFIG: TenantConfig = {
  sessionSampleRate: 50,
  sessionReplaySampleRate: 0,
};

const getApplicationStartContext = (pathname: string | null) => ({
  environment: config.datadog.environment,
  apiHost: new URL(window.location.origin).toString(),
  release: config.datadog.release,
  service: config.datadog.service,
  pathname,
});

export const useApplicationStartLogger = (pathname: string | null) => {
  useEffect(() => {
    logToDatadog(`Application started`, `info`, getApplicationStartContext(pathname));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only log on initial mount
};

/**
 * Hook for initializing Datadog
 * This combines the functionality of the original useDatadog and useSentry hooks
 */
type UseDatadogOptions = {
  isPovTenantEnabled?: boolean;
};

export const useDatadog = ({isPovTenantEnabled = false}: UseDatadogOptions = {}) => {
  const {data: session, status} = useSession() || {};
  // Create a ref to track if Datadog has been initialized
  const datadogInitialized = useRef<boolean>(false);

  useEffect(() => {
    // Skip initialization if already done
    if (datadogInitialized.current) return;

    // Don't initialize if prerequisites aren't met
    if (!session?.user || !config || !config.datadog) {
      return;
    }

    // Get environment from config
    const env = config.datadog.environment;

    // Computed once and shared across getDatadogUserData and the Groundcover user block below.
    const highestPrivilegeRole = getHighestPrivilegeRole(session.user.roles).role;

    const getDatadogUserData = (user: typeof session.user, tenant: typeof session.tenant) => ({
      id: user.email,
      email: user.email,
      name: user.name || undefined,
      tenant_id: tenant.id,
      tenant_name: tenant.slug,
      tenant_display_name: tenant.displayName,
      roles: user.roles,
      role: highestPrivilegeRole,
    });

    // Shared session classification used by both Datadog RUM and Groundcover RUM.
    const PROD_TEST_TENANTS = new Set(config.datadog.testTenants || []);
    const isTestTenant = session?.tenant?.id && PROD_TEST_TENANTS.has(session.tenant.id);
    const isLinxAdmin = isLinxAdminEmail(session.user.email);

    // Default RUM settings from config file
    const DEFAULT_CONFIG: TenantConfig = {
      sessionSampleRate: config.datadog.sessionSampleRate,
      sessionReplaySampleRate: config.datadog.sessionReplaySampleRate,
    };
    // Start with default configuration
    let rumConfig = {...DEFAULT_CONFIG};

    // Apply test tenant configuration if applicable
    if (isTestTenant) {
      rumConfig = {
        ...rumConfig,
        ...TEST_TENANT_CONFIG,
      };
    }

    // Disable session replay for linx.admin users
    if (isLinxAdmin) {
      rumConfig = {
        ...rumConfig,
        sessionReplaySampleRate: 0,
      };
    }

    // Initialize logs if enabled in config
    if (config.datadog.enabled && config.datadog.clientToken) {
      try {
        // Check if Datadog Logs is already initialized to prevent multiple initializations
        // This will prevent the "DD_LOGS is already initialized" warning
        if (!window.__DD_LOGS_INITIALIZED) {
          datadogLogs.init({
            clientToken: config.datadog.clientToken,
            site: config.datadog.site as any,
            forwardErrorsToLogs: true,
            sessionSampleRate: config.datadog.sessionSampleRate,
            service: config.datadog.service,
            env,
            version: config.datadog.release,
          });
          // Mark as initialized to prevent multiple initializations
          window.__DD_LOGS_INITIALIZED = true;
        }

        datadogLogs.setGlobalContextProperty('domain', window.location.hostname);

        // Set user and context information for logs
        if (session.user && session.tenant) {
          datadogLogs.setUser(getDatadogUserData(session.user, session.tenant));
        }
      } catch (error) {
        console.error(`Failed to initialize Datadog Logs:`, error);
      }
    }

    // Only initialize RUM if enabled in config and when authenticated
    if (
      config.datadog.enabled &&
      status === `authenticated` &&
      !window._DATADOG_SYNTHETICS_BROWSER &&
      config.datadog.clientToken &&
      config.datadog.applicationId &&
      config.datadog.environment &&
      config.datadog.release
    ) {
      const applicationId = config.datadog.applicationId;
      const clientToken = config.datadog.clientToken;

      // Get RUM configuration from config file
      const RUM_CONFIG = {
        applicationId,
        clientToken,
        site: config.datadog.site,
        service: config.datadog.service,
        defaultPrivacyLevel: `mask-user-input`,
      } as const;

      /**
       * to make sure we start session replay recording manually, we first need to set the startSessionReplayRecordingManually flag to true.
       * this will ensure that the session replay recording will only start when we call the startSessionReplayRecording method manually.
       * if we don't set this flag, the session replay recording might start automatically when the page is loaded (if session is sampled).
       */
      const shouldStartSessionReplayRecording = isPovTenantEnabled && !isLinxAdmin;

      if (shouldStartSessionReplayRecording) {
        rumConfig = {
          ...rumConfig,
          startSessionReplayRecordingManually: true,
        };
      }

      try {
        // Check if Datadog RUM is already initialized to prevent multiple initializations
        if (!window.__DD_RUM_INITIALIZED) {
          datadogRum.init({
            ...RUM_CONFIG,
            env,
            trackResources: true,
            trackLongTasks: true,
            trackUserInteractions: true,
            version: config.datadog.release,
            ...rumConfig,
          });
          // Mark as initialized to prevent multiple initializations
          window.__DD_RUM_INITIALIZED = true;
        }

        if (shouldStartSessionReplayRecording) {
          datadogRum.startSessionReplayRecording?.();
          logToDatadog(`Datadog RUM session recording started manually for tenant ${session.tenant.id}`, LogLevel.INFO);
        }

        // Set global context that will be attached to all RUM events
        datadogRum.setGlobalContextProperty('auth_status', status);
        datadogRum.setGlobalContextProperty('domain', window.location.hostname);

        // Set user and context information
        if (session.user && session.tenant) {
          datadogRum.setUser(getDatadogUserData(session.user, session.tenant));
        }
      } catch (error) {
        console.error(`Failed to initialize Datadog RUM:`, error);
      }
    }
    // Initialize Groundcover RUM alongside Datadog if configured.
    if (
      config.groundcover.enabled &&
      status === `authenticated` &&
      // session.tenant is always an object (auth-options builds it); only its fields can be missing.
      session.tenant.id &&
      config.groundcover.apiKey &&
      !window._DATADOG_SYNTHETICS_BROWSER
    ) {
      const {apiKey, dsn, appId, cluster, release} = config.groundcover;

      try {
        if (!window.__GC_RUM_INITIALIZED) {
          const {sessionSampleRate = 0, sessionReplaySampleRate = 0} = rumConfig;
          // Datadog is the source of truth. Groundcover has no replay sample rate, so roll it here.
          // ponytail: rolled per page load, not per session; exact only for 0/100, persist the roll if a fractional rate is ever set.
          const gcReplayEnabled = Math.random() * 100 < sessionReplaySampleRate;
          let gcReplayRecording = false;
          groundcover.init({
            apiKey,
            dsn,
            appId,
            cluster,
            environment: env,
            releaseId: release,
            user: {
              id: session.user.email,
              email: session.user.email,
              name: session.user.name || undefined,
              role: highestPrivilegeRole ?? undefined,
              organization: session.tenant.slug,
              properties: {
                tenant_id: session.tenant.id,
                tenant_name: session.tenant.slug,
                tenant_display_name: session.tenant.displayName,
                roles: session.user.roles,
                domain: window.location.hostname,
              },
            },
            options: {
              // Groundcover sample rates are 0..1 fractions; Datadog uses 0..100 percent.
              sessionSampleRate: sessionSampleRate / 100,
              beforeSend: event =>
                !(event.type === `network` && event.attributes.resource_name.startsWith(`/_next/static/`)),
              // groundcover has no queryable has-replay field; the session-replay monitor keys off this tag.
              enrichEvent: event => {
                const redacted = redactCredentialWriteBody(event);
                return {
                  ...redacted,
                  attributes: {...redacted.attributes, replay: gcReplayRecording},
                } as typeof redacted;
              },
              privacy: {
                maskSelectors: [`input`, `textarea`, `[contenteditable]`],
                sensitiveKeys: [
                  `private_key`,
                  `privatekey`,
                  `passphrase`,
                  `access_token`,
                  `accessToken`,
                  `id_token`,
                  `refresh_token`,
                  `authorization`,
                  `cookie`,
                  `password`,
                  // Substrings of secret field names the SDK's patterns miss: connector credentials, the self-registration code.
                  `key`,
                  `cert`,
                  `pem`,
                  `pass_phrase`,
                  `header_value`,
                  `service_account`,
                  `access_code`,
                ],
                // Response bodies carry customer data under any key, so every value is redacted.
                redact: ({kind, value}) =>
                  kind === `response-body` || (typeof value === `string` && SECRET_VALUE.test(value))
                    ? `[REDACTED]`
                    : undefined,
              },
              enabledEvents: [
                `dom`,
                `network`,
                `exceptions`,
                `logs`,
                `pageload`,
                `navigation`,
                `performance`,
                ...(gcReplayEnabled ? ([`replay`] as const) : []),
              ],
            },
          });
          window.__GC_RUM_INITIALIZED = true;
          // useApplicationStartLogger fires on mount, before the session loads and Groundcover inits, so mirror it here.
          logToGroundcover(LogLevel.INFO, `Application started`, getApplicationStartContext(window.location.pathname));
          // The SDK never auto-starts replay, and startReplayRecording() ignores enabledEvents, so gate the call here.
          if (gcReplayEnabled) {
            groundcover.startReplayRecording();
            gcReplayRecording = true;
            // ponytail: sent once per page load; the monitor reads the `replay` tag instead, which survives session rotation (4h / 30m idle).
            sendGroundcoverEvent(`session_replay_started`, {});
          }
        }
      } catch (error) {
        logger?.error({error}, `Failed to initialize Groundcover RUM:`);
      }
    }

    // Mark as initialized
    datadogInitialized.current = true;
  }, [isPovTenantEnabled, session, status]);

  return {
    captureException,
    captureMessage,
    logToDatadog,
  };
};
